import React, { useState, useEffect } from 'react';
import { X, Calendar, TrendingUp, Wallet, DollarSign, TrendingDown, CheckCircle, ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import AmountInput from './AmountInput';

const RevenueWizard = ({ isOpen, onClose, onSubmit, editMode = false, initialData = null, defaultType = null, onSwitchToCashWizard }) => {
  const [currentStep, setCurrentStep] = useState(editMode ? 4 : (defaultType ? 2 : 1));
  
  const reverseTypeMapping = {
    'chiffre_affaires': 'ca',
    'revenu_espece': 'cash',
    'revenu_caisse': 'caisse',
    'benefice_theorique': 'benefice_theorique',
    'benefice_net': 'benefice_net'
  };

  const [formData, setFormData] = useState(
    editMode && initialData
      ? {
          type: reverseTypeMapping[initialData.type] || initialData.type || '',
          period: '',
          periodType: '',
          customDates: {
            start: initialData.date_start ? new Date(initialData.date_start) : null,
            end: initialData.date_end ? new Date(initialData.date_end) : null
          },
          amount: initialData.amount || 0,
          note: initialData.notes || ''
        }
      : {
          type: defaultType || '',
          period: '',
          periodType: '',
          customDates: { start: null, end: null },
          amount: 0,
          note: ''
        }
  );
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectingDateType, setSelectingDateType] = useState('start');

  // Failsafe counter to completely prevent infinite loops
  const renderCount = React.useRef(0);
  
  // Réinitialiser ou pré-remplir selon le mode
  React.useEffect(() => {
    if (isOpen) {
      renderCount.current += 1;
      if (renderCount.current > 5) {
        console.error('FAILSAFE TRIGGERED in RevenueWizard! Too many updates.');
        return;
      }
      if (editMode && initialData) {
        setCurrentStep(4);
        setFormData({
          type: reverseTypeMapping[initialData.type] || initialData.type || '',
          period: '',
          periodType: '',
          customDates: {
            start: initialData.date_start ? new Date(initialData.date_start) : null,
            end: initialData.date_end ? new Date(initialData.date_end) : null
          },
          amount: initialData.amount || 0,
          note: initialData.notes || ''
        });
      } else {
        setCurrentStep(defaultType ? 2 : 1);
        setFormData({
          type: defaultType || '',
          period: '',
          periodType: '',
          customDates: { start: null, end: null },
          amount: 0,
          note: ''
        });
      }
      setCalendarMonth(new Date());
      setSelectingDateType('start');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editMode, initialData]);

  // Types d'entrée avec leurs configurations
  const entryTypes = [
    {
      id: 'ca',
      label: "Chiffre d'affaires",
      description: "Ventes & recettes",
      icon: TrendingUp,
      color: 'bg-emerald-500',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200'
    },
    {
      id: 'cash',
      label: "Cash flow restant",
      description: "Encaissement liquide",
      icon: Wallet,
      color: 'bg-lime-600',
      bgColor: 'bg-lime-50',
      borderColor: 'border-lime-200'
    },
    {
      id: 'caisse',
      label: "Entrée cash",
      description: "Recette en caisse",
      icon: DollarSign,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      id: 'benefice_theorique',
      label: "Bénéfice Théorique",
      description: "Profit estimé",
      icon: TrendingDown,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    {
      id: 'benefice_net',
      label: "Bénéfice Net",
      description: "Profit réel final",
      icon: CheckCircle,
      color: 'bg-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200'
    }
  ];

  const quickPeriods = [
    { id: 'today', label: "Aujourd'hui" },
    { id: 'week', label: "Cette semaine" },
    { id: 'month', label: "Ce mois" },
    { id: 'global', label: "Global" },
    { id: 'custom', label: "Personnalisé" }
  ];

  const handleTypeSelect = (typeId) => {
    if (typeId === 'caisse' && onSwitchToCashWizard && !editMode) {
      onSwitchToCashWizard();
      return;
    }
    setFormData({ ...formData, type: typeId });
  };

  const handlePeriodSelect = (periodId) => {
    setFormData({ ...formData, periodType: periodId });
    if (periodId !== 'custom') {
      // Reset custom dates if switching away from custom
      setFormData(prev => ({ ...prev, customDates: { start: null, end: null } }));
    }
  };

  const handleDateSelect = (date) => {
    if (selectingDateType === 'start') {
      setFormData(prev => ({
        ...prev,
        customDates: { ...prev.customDates, start: date, end: null }
      }));
      setSelectingDateType('end');
    } else {
      setFormData(prev => ({
        ...prev,
        customDates: { ...prev.customDates, end: date }
      }));
    }
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const startDayOfWeek = getDay(monthStart);
    
    // Jours de la semaine commençant par Samedi
    const weekDays = ['S', 'D', 'L', 'M', 'M', 'J', 'V'];
    
    // Ajuster pour commencer par Samedi
    const adjustedStartDay = (startDayOfWeek + 1) % 7;
    
    return (
      <div className="bg-gray-50 rounded-2xl p-6">
        {/* Header du calendrier */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <ChevronLeft size={20} />
          </button>
          <h4 className="text-xl font-bold text-gray-900">
            {format(calendarMonth, 'MMMM yyyy', { locale: fr })}
          </h4>
          <button
            onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Jours de la semaine */}
        <div className="grid grid-cols-7 gap-2 mb-3">
          {weekDays.map((day, idx) => (
            <div key={idx} className="text-center text-sm font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Jours du mois */}
        <div className="grid grid-cols-7 gap-2">
          {/* Cellules vides pour aligner le premier jour */}
          {Array.from({ length: adjustedStartDay }).map((_, idx) => (
            <div key={`empty-${idx}`} />
          ))}
          
          {/* Jours du mois */}
          {daysInMonth.map((day) => {
            const isStart = formData.customDates.start && 
              format(day, 'yyyy-MM-dd') === format(formData.customDates.start, 'yyyy-MM-dd');
            const isEnd = formData.customDates.end && 
              format(day, 'yyyy-MM-dd') === format(formData.customDates.end, 'yyyy-MM-dd');
            const isInRange = formData.customDates.start && formData.customDates.end &&
              day > formData.customDates.start && day < formData.customDates.end;
            
            return (
              <button
                key={day.toISOString()}
                onClick={() => handleDateSelect(day)}
                className={`
                  h-12 rounded-xl font-medium text-base transition-all
                  ${isStart || isEnd
                    ? 'bg-emerald-500 text-white'
                    : isInRange
                    ? 'bg-emerald-100 text-emerald-900'
                    : 'text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>

        {/* Message d'aide */}
        <div className="mt-6 text-center text-gray-600 text-sm">
          {selectingDateType === 'start' ? 'Sélectionnez la date de début' : 'Sélectionnez la date de fin'}
        </div>
      </div>
    );
  };

  const handleAmountChange = (value) => {
    setFormData({ ...formData, amount: value });
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    onSubmit(formData);
    onClose();
  };

  if (!isOpen) return null;

  const selectedType = entryTypes.find(t => t.id === formData.type);
  const today = format(new Date(), 'dd MMM yyyy', { locale: fr });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center rounded-t-2xl z-10">
          <div>
            <h2 className="text-2xl font-black text-gray-900">
              {editMode ? 'Modifier l\'entrée' : 'Nouvelle entrée'}
            </h2>
            {(defaultType || formData.type) && (
              <p className="text-lg font-bold text-emerald-600 mt-1">
                {entryTypes.find(t => t.id === (defaultType || formData.type))?.label}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">{today}</span>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Stepper */}
        <div className="px-6 py-3 border-b border-gray-100">
          <div className="flex items-center justify-between max-w-xl mx-auto">
            {[1, 2, 3, 4].map((step, index) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center gap-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                      step < currentStep
                        ? 'bg-emerald-500 text-white'
                        : step === currentStep
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {step < currentStep ? '✓' : step}
                  </div>
                  <span
                    className={`text-xs font-medium ${
                      step <= currentStep ? 'text-emerald-600' : 'text-gray-400'
                    }`}
                  >
                    {step === 1 ? 'Type' : step === 2 ? 'Période' : step === 3 ? 'Montant' : 'Validation'}
                  </span>
                </div>
                {index < 3 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      step < currentStep ? 'bg-emerald-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {/* Étape 1 - Type */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Quel type d'entrée ?</h3>
                <p className="text-sm text-gray-500">Choisissez la catégorie</p>
              </div>

              <div className="space-y-3">
                {formData.type ? (
                  <>
                    {entryTypes.filter(t => t.id === formData.type).map((type) => {
                      const Icon = type.icon;
                      return (
                        <div key={type.id} className={`w-full p-3 rounded-xl border-2 ${type.bgColor} ${type.borderColor} flex items-center gap-3`}>
                          <div className={`w-10 h-10 rounded-lg ${type.color} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="text-white" size={28} />
                          </div>
                          <div className="text-left flex-1">
                            <div className="font-bold text-lg text-gray-900">{type.label}</div>
                            <div className="text-gray-600">{type.description}</div>
                          </div>
                        </div>
                      );
                    })}
                    <button type="button" onClick={() => setFormData({ ...formData, type: '' })}
                      className="w-full py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-600 font-medium hover:bg-gray-50 flex items-center justify-center gap-2 transition-all">
                      <X size={16} /> Changer de catégorie
                    </button>
                  </>
                ) : (
                  entryTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => handleTypeSelect(type.id)}
                        className="w-full p-3 rounded-xl border-2 bg-white border-gray-200 hover:border-gray-300 transition-all flex items-center gap-3"
                      >
                        <div className={`w-10 h-10 rounded-lg ${type.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="text-white" size={28} />
                        </div>
                        <div className="text-left flex-1">
                          <div className="font-bold text-lg text-gray-900">{type.label}</div>
                          <div className="text-gray-600">{type.description}</div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <span />
                <span className="text-xs text-gray-500 font-medium">Étape 1 sur 4</span>
                <button
                  onClick={handleNext}
                  disabled={!formData.type}
                  className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Continuer
                  <ArrowRight size={16} />
                </button>
              </div>

            </div>
          )}

          {/* Étape 2 - Période */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Quelle période ?</h3>
                <p className="text-gray-500">Sélection rapide ou dates personnalisées</p>
              </div>

              <div className="flex flex-wrap gap-3">
                {quickPeriods.map((period) => {
                  const isSelected = formData.periodType === period.id;
                  return (
                    <button
                      key={period.id}
                      onClick={() => handlePeriodSelect(period.id)}
                      className={`px-6 py-3 rounded-full font-medium transition-all ${
                        isSelected
                          ? 'bg-emerald-50 text-emerald-700 border-2 border-emerald-300'
                          : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {period.label}
                    </button>
                  );
                })}
              </div>

              {/* Afficher le résumé ou le calendrier selon la sélection */}
              {formData.periodType === 'today' && (
                <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-3">
                  <Calendar className="text-emerald-600" size={24} />
                  <span className="font-medium text-gray-900">Aujourd'hui · {today}</span>
                </div>
              )}

              {formData.periodType === 'custom' && (
                <>
                  {renderCalendar()}
                  
                  {/* Bouton "Sélectionnez une plage" */}
                  {formData.customDates.start && formData.customDates.end && (
                    <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-3">
                      <Calendar className="text-emerald-600" size={24} />
                      <span className="font-medium text-gray-900">
                        {format(formData.customDates.start, 'dd MMM', { locale: fr })} → {format(formData.customDates.end, 'dd MMM yyyy', { locale: fr })}
                      </span>
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <button
                  onClick={handleBack}
                  className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Retour
                </button>
                <span className="text-xs text-gray-500 font-medium">Étape 2 sur 4</span>
                <button
                  onClick={handleNext}
                  disabled={!formData.periodType || (formData.periodType === 'custom' && (!formData.customDates.start || !formData.customDates.end))}
                  className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Continuer
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Étape 3 - Montant */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Quel montant ?</h3>
                <p className="text-gray-500">En dinars algériens (DA)</p>
              </div>

              <AmountInput value={formData.amount} onChange={handleAmountChange} accentColor="green" quickAmounts={[10000, 50000, 100000]} />

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">Note (Optionnel)</label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="Ex : ventes du mois de mars..."
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500 resize-none"
                  rows="3"
                />
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <button
                  onClick={handleBack}
                  className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Retour
                </button>
                <span className="text-xs text-gray-500 font-medium">Étape 3 sur 4</span>
                <button
                  onClick={handleNext}
                  className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 transition-colors"
                >
                  Continuer
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Étape 4 - Validation */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Tout est correct ?</h3>
                <p className="text-gray-500">Relisez avant de valider</p>
              </div>

              {/* Résumé */}
              <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-600">Type</span>
                  <span className="font-semibold text-gray-900">{selectedType?.label}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-600">Période</span>
                  <span className="font-semibold text-gray-900">Aujourd'hui · {today}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-gray-600">Montant</span>
                  <span className="font-bold text-2xl text-emerald-600">{formData.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA</span>
                </div>
              </div>

              {/* Message d'info */}
              <div className="bg-emerald-50 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle className="text-emerald-600 flex-shrink-0" size={24} />
                <span className="text-emerald-700 font-medium">Sera enregistré dans votre tableau de bord</span>
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <button
                  onClick={handleBack}
                  className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Modifier
                </button>
                <span className="text-xs text-gray-500 font-medium">Étape 4 sur 4</span>
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 transition-colors"
                >
                  <CheckCircle size={16} />
                  Enregistrer
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default RevenueWizard;
